// 클럽 신청 시트 — "우리 클럽도 시작하기" 진입점.
//
// B2B 온보딩 입구. 폼 입력 → /api/v1/public/club-signup으로 전송하면
// 페어매치팀 텔레그램 알림이 트리거되고, 1~2일 내 수동 클럽 생성 플로우가
// 이어진다. 인증·세션 없이 동작하므로 fetch를 직접 호출(api 래퍼 X).

import { useEffect, useRef, useState } from "preact/hooks";
import { safeJson } from "../api";
import { DEFAULT_CONFIG, state } from "../state";
import { attachPhoneFormatter } from "../ui/format";
import { mountPreact } from "../ui/preact-mount";
import { closeSheet, openSheet, toast, withBusy } from "../ui/sheet";

const ROLE_OPTIONS: Array<[string, string]> = [
  ["president", "회장"],
  ["staff", "운영진"],
  ["member", "일반 회원"],
];
const SIZE_OPTIONS: Array<[string, string]> = [
  ["u20", "20명 이하"],
  ["20_40", "20~40명"],
  ["40_80", "40~80명"],
  ["80_plus", "80명 이상"],
];

function ChipPicker({
  options,
  value,
  onChange,
}: {
  options: Array<[string, string]>;
  value: string | null;
  onChange: (v: string | null) => void;
}) {
  return (
    <div class="chip-row">
      {options.map(([v, lbl]) => {
        const sel = value === v;
        return (
          <button
            key={v}
            type="button"
            class={"chip" + (sel ? " chip--selected" : "")}
            onClick={() => onChange(sel ? null : v)}
          >
            {lbl}
          </button>
        );
      })}
    </div>
  );
}

function ClubSignupForm() {
  const [clubName, setClubName] = useState("");
  const [name, setName] = useState("");
  const [memo, setMemo] = useState("");
  const [role, setRole] = useState<string | null>(null);
  const [size, setSize] = useState<string | null>(null);

  const contactRef = useRef<HTMLInputElement>(null);
  const submitRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (contactRef.current) attachPhoneFormatter(contactRef.current);
  }, []);

  async function onSubmit(e: Event) {
    e.preventDefault();
    const cName = clubName.trim();
    const n = name.trim();
    const contact = contactRef.current?.value.trim() || "";
    if (!cName) { toast("클럽명을 입력해주세요"); return; }
    if (!n) { toast("이름을 입력해주세요"); return; }
    if (!contact) { toast("연락처를 입력해주세요"); return; }
    if (!role) { toast("역할을 선택해주세요"); return; }
    const body = {
      club_name: cName,
      name: n,
      contact,
      role,
      size_band: size,
      memo: memo.trim() || null,
      referrer: (typeof window !== "undefined" && window.location) ? window.location.href : "",
    };
    try {
      await withBusy(submitRef.current, "보내는 중…", async () => {
        const base = (state.config.apiBase || DEFAULT_CONFIG.apiBase).replace(/\/$/, "");
        const resp = await fetch(`${base}/api/v1/public/club-signup`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        const text = await resp.text();
        const payload = text ? safeJson(text) : null;
        if (!resp.ok) {
          const err = (payload && (payload.error || payload)) || {};
          throw new Error(err.message || `요청 실패 (${resp.status})`);
        }
        closeSheet();
        toast("신청 받았어요!\n페어매치팀이 1~2일 내 연락드릴게요");
      });
    } catch (err: any) { toast(err.message); }
  }

  return (
    <form class="form" onSubmit={onSubmit}>
      <p class="muted small" style="margin:0 0 4px">페어매치팀이 곧 문자로 연락드릴게요.</p>
      <div class="field">
        <span class="field__label">클럽명</span>
        <input
          class="input"
          type="text"
          maxLength={60}
          autocomplete="off"
          value={clubName}
          onInput={(e) => setClubName((e.currentTarget as HTMLInputElement).value)}
        />
      </div>
      <div class="field">
        <span class="field__label">이름</span>
        <input
          class="input"
          type="text"
          maxLength={30}
          placeholder="홍길동"
          autocomplete="off"
          value={name}
          onInput={(e) => setName((e.currentTarget as HTMLInputElement).value)}
        />
      </div>
      <div class="field">
        <span class="field__label">연락처 (전화번호)</span>
        <input
          ref={contactRef}
          class="input"
          type="tel"
          maxLength={30}
          placeholder="번호만 입력해주세요"
          autocomplete="off"
          inputmode="tel"
        />
      </div>
      <div class="field">
        <span class="field__label">역할</span>
        <ChipPicker options={ROLE_OPTIONS} value={role} onChange={setRole} />
      </div>
      <div class="field">
        <span class="field__label">클럽 인원 (선택)</span>
        <ChipPicker options={SIZE_OPTIONS} value={size} onChange={setSize} />
      </div>
      <div class="field">
        <span class="field__label">메모 (선택)</span>
        <textarea
          class="input"
          maxLength={200}
          rows={2}
          placeholder="전하고 싶은 내용 (선택)"
          style="resize:vertical;min-height:60px"
          value={memo}
          onInput={(e) => setMemo((e.currentTarget as HTMLTextAreaElement).value)}
        />
      </div>
      <button ref={submitRef} class="btn btn--primary btn--block" type="submit">신청하기</button>
    </form>
  );
}

export function openClubSignupSheet(): void {
  openSheet("우리 클럽도 시작하기", mountPreact(<ClubSignupForm />));
}
